import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { find } from 'ramda';
import { COLOR } from '~~elements/Const';
import Button from '~~elements/Button';
import toast from '~~elements/Toast';
import IconBlockSVG from '~~elements/static/IconBlock.svg';
import IconCircleCheckedSVG from '~~elements/static/IconCircleChecked.svg';
import { TeamInSeasonRegistryResource } from '~~apis/resource';
import UTRMultilingual from '../../Multilingual';
import ProgramPrint from '../../../ProgramPrint';
import RMultilingual from '../../../Tournament/Registry/Multilingual';
import { useRegistryDispatch } from '../../useUserTournamentRegistry';

const StyledDiv = styled.div`
  width: 100%;
  margin: 2rem 0 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  text-align: start;
  .front-check {
    &--title {
      font-size: 1.2rem;
      font-weight: bold;
      margin: 0 0 0.8rem;
    }
    &--missing {
      margin: 0;
      padding: 0.2rem;
      font-size: 0.9rem;
      color: red;
    }
    &--result {
      display: flex;
      align-items: center;
      .comment {
        font-size: 1.1rem;
        font-weight: bold;
      }
      .icon {
        width: 1.2rem;
        height: 1.2rem;
        margin: 0 0.5rem 0 0;
      }
    }
  }
  .status {
    margin-top: 1rem;
    &--current {
      font-size: 1.2rem;
      font-weight: bold;
      margin: 0 0 0.8rem;
    }
    &--list {
      display: flex;
      align-items: center;
      justify-content: start;
      flex-wrap: wrap;
      gap: 0.5rem;
    }
    &--action {
      margin-top: 0.8rem;
      display: flex;
      align-items: center;
      justify-content: start;
      gap: 1rem;
      flex-wrap: wrap;
    }
  }
`;

const ArrowDiv = styled.div`
  height: 3rem;
  width: 11rem;
  display: flex;
  align-items: center;
  justify-content: start;
  padding: 0.5rem 0.5rem 0.5rem 1rem;
  background: linear-gradient(
        135deg,
        transparent 0,
        ${(props) => (props.isCurrent ? COLOR.primary : COLOR.secondary)} 0
      )
      top left,
    linear-gradient(-135deg, transparent 1.5rem, ${(props) => (props.isCurrent ? COLOR.primary : COLOR.secondary)} 0)
      top right,
    linear-gradient(-45deg, transparent 1.5rem, ${(props) => (props.isCurrent ? COLOR.primary : COLOR.secondary)} 0)
      bottom right,
    linear-gradient(45deg, transparent 0, ${(props) => (props.isCurrent ? COLOR.primary : COLOR.secondary)} 0) bottom
      left;
  background-size: 50% 50%;
  background-repeat: no-repeat;
  border-radius: 2px;
  color: white;
  font-size: 1rem;
  font-weight: bold;
`;

const teamRequirements = [
  { key: 'name', display: RMultilingual(`DEFAULT_COLUMN.TEAM.NAME.NAME`) },
  { key: 'city', display: RMultilingual(`DEFAULT_COLUMN.TEAM.CITY.NAME`) },
  { key: 'icon_path', display: RMultilingual(`DEFAULT_COLUMN.TEAM.ICON.NAME`) },
  { key: 'contact_person', display: RMultilingual(`DEFAULT_COLUMN.TEAM.CONTACT_PERSON.NAME`) },
  { key: 'contact_phone', display: RMultilingual(`DEFAULT_COLUMN.TEAM.CONTACT_PHONE.NAME`) },
];

const playerRequirements = [
  { key: 'name', display: RMultilingual(`DEFAULT_COLUMN.PLAYER.NAME.NAME`) },
  { key: 'number', display: RMultilingual(`DEFAULT_COLUMN.PLAYER.NUMBER.NAME`) },
  { key: 'avatar_path', display: RMultilingual(`DEFAULT_COLUMN.PLAYER.AVATAR.NAME`) },
  { key: 'birthday', display: RMultilingual(`DEFAULT_COLUMN.PLAYER.BIRTHDAY.NAME`) },
  { key: 'first_position', display: RMultilingual(`DEFAULT_COLUMN.PLAYER.FIRST_POSITION.NAME`) },
  { key: 'second_position', display: RMultilingual(`DEFAULT_COLUMN.PLAYER.SECOND_POSITION.NAME`) },
  { key: 'p_hand', display: RMultilingual(`DEFAULT_COLUMN.PLAYER.P_HAND.NAME`) },
  { key: 'b_hand', display: RMultilingual(`DEFAULT_COLUMN.PLAYER.B_HAND.NAME`) },
];

const FreeTournamentStatusList = ['SIGNED_UP', 'REVIEW_REQUESTED', 'COMPLETED'];
const PaidTournamentStatusList = [
  'SIGNED_UP',
  'REVIEW_REQUESTED',
  'REVIEW_COMPLETED',
  'PAYMENT_CHECK_REQUESTED',
  'COMPLETED',
];

const ReviewSection = ({ teamInRegistry, registry }) => {
  const [checkResult, setCheckResult] = useState([]);
  const [sendingReview, setSendingReview] = useState(false);
  const [showProgramPrint, setShowProgramPrint] = useState(false);
  const dispatch = useRegistryDispatch();

  useEffect(() => {
    const newCheckResult = [];

    // 球隊驗證
    teamRequirements.forEach((requirement) => {
      if (!!teamInRegistry[requirement.key]) {
        return;
      }

      if (!!teamInRegistry.connected_team && teamInRegistry.connected_team[requirement.key]) {
        return;
      }

      newCheckResult.push(`${UTRMultilingual(`CUSTOM.TEAM_SIDEBAR.TEAM`)} ${requirement.display}`);
      return;
    });

    registry.team_columns.forEach((teamColumn) => {
      if (!teamColumn.is_required) {
        return;
      }

      const filledColumn = find(
        (c) => c.name === teamColumn.name && c.type === teamColumn.type,
        teamInRegistry.columns,
      );
      if (filledColumn && filledColumn.value) {
        return;
      }

      newCheckResult.push(`${UTRMultilingual(`CUSTOM.TEAM_SIDEBAR.TEAM`)} ${teamColumn.name}`);
      return;
    });

    // 隨隊人員驗證
    registry.staff_titles.forEach((teamStaff) => {
      if (!teamStaff.is_required) {
        return;
      }

      const filledStaff = find((s) => s.title === teamStaff.title, teamInRegistry.staffs);
      if (!filledStaff) {
        newCheckResult.push(`${UTRMultilingual(`CUSTOM.TEAM_SIDEBAR.STAFF`)} ${teamStaff.title}`);
        return;
      }

      teamStaff.columns.forEach((staffColumn) => {
        if (!staffColumn.is_required) {
          return;
        }

        const filledColumn = find(
          (c) => c.name === staffColumn.name && c.type === staffColumn.type,
          filledStaff.columns,
        );
        if (filledColumn && filledColumn.value) {
          return;
        }

        newCheckResult.push(`${UTRMultilingual(`CUSTOM.TEAM_SIDEBAR.STAFF`)} ${teamStaff.title}-${staffColumn.name}`);
        return;
      });
    });

    // 球員驗證
    teamInRegistry.players.forEach((player) => {
      playerRequirements.forEach((requirement) => {
        if (!!player[requirement.key]) {
          return;
        }

        newCheckResult.push(`${UTRMultilingual(`CUSTOM.TEAM_SIDEBAR.PLAYER`)} ${player.name}-${requirement.display}`);
        return;
      });

      registry.player_columns.forEach((playerColumn) => {
        if (!playerColumn.is_required) {
          return;
        }

        const filledColumn = find((c) => c.name === playerColumn.name && c.type === playerColumn.type, player.columns);
        if (filledColumn && filledColumn.value) {
          return;
        }

        newCheckResult.push(`${UTRMultilingual(`CUSTOM.TEAM_SIDEBAR.PLAYER`)} ${player.name}-${playerColumn.name}`);
        return;
      });
    });

    setCheckResult(newCheckResult);
  }, [teamInRegistry, registry]);

  const sendReviewAPI = (action) => {
    const data = { action };
    setSendingReview(true);
    TeamInSeasonRegistryResource.updateReview({
      registryUniqid: registry.uniqid,
      teamInRegistryUniqid: teamInRegistry.uniqid,
      data,
    })
      .then(({ data: newTeamInRegistry }) => {
        dispatch({
          type: 'UPDATE_TEAM_IN_REGISTRY',
          payload: newTeamInRegistry,
        });
        toast(UTRMultilingual(`CUSTOM.REVIEW.SENT`), { status: 'success' });
      })
      .catch(() => {
        toast(UTRMultilingual(`COMMON.UNKNOWN_ERROR`), { status: 'error' });
      })
      .finally(() => {
        setSendingReview(false);
      });
  };
  const sendReviewRequest = () => {
    sendReviewAPI('REVIEW');
  };
  const sendRevokeRequest = () => {
    sendReviewAPI('REVOKE');
  };

  const collapseProgramPrint = () => {
    setShowProgramPrint(!showProgramPrint);
  };

  const getActionButton = () => {
    switch (teamInRegistry.status) {
      case 'SIGNED_UP':
        return (
          <Button
            title={UTRMultilingual(`CUSTOM.REVIEW.SEND_REVIEW_REQUESTED_DESCRIPTION`)}
            width="fix-content"
            color="red"
            disabled={checkResult.length > 0 || sendingReview}
            onClick={sendReviewRequest}
          >
            {UTRMultilingual(`CUSTOM.REVIEW.SEND_REVIEW_REQUESTED`)}
          </Button>
        );
      case 'REVIEW_REQUESTED':
        return (
          <Button width="fix-content" color="red" onClick={sendRevokeRequest} disabled={sendingReview}>
            {UTRMultilingual(`CUSTOM.REVIEW.REVOKE`)}
          </Button>
        );
      case 'REVIEW_COMPLETED':
        return (
          <Button width="fix-content" color="red" onClick={sendReviewRequest} disabled={sendingReview}>
            {UTRMultilingual(`CUSTOM.REVIEW.SEND_PAYMENT_CHECK_REQUESTED`)}
          </Button>
        );
      case 'PAYMENT_CHECK_REQUESTED':
        return (
          <Button width="fix-content" color="red" onClick={sendRevokeRequest} disabled={sendingReview}>
            {UTRMultilingual(`CUSTOM.REVIEW.REVOKE`)}
          </Button>
        );
      default:
        return <></>;
    }
  };

  return (
    <StyledDiv>
      <div className="front-check">
        <h4 className="front-check--title">{UTRMultilingual(`CUSTOM.REVIEW.COMPLETION_CHECK`)}</h4>
        {checkResult.map((result, i) => (
          <div key={i} className="front-check--missing">
            {`${result} ${UTRMultilingual(`CUSTOM.REVIEW.RESULT_NOT_COMPLETED`)}`}
          </div>
        ))}
        {checkResult.length === 0 ? (
          <div className="front-check--result">
            <img className="icon" src={IconCircleCheckedSVG} alt="passed" />
            <span>{UTRMultilingual(`CUSTOM.REVIEW.COMPLETED`)}</span>
          </div>
        ) : (
          <div className="front-check--result">
            <img className="icon" src={IconBlockSVG} alt="block" />
            <span className="comment">{UTRMultilingual(`CUSTOM.REVIEW.MISSING`)}</span>
          </div>
        )}
      </div>
      <div className="status">
        <h4 className="status--current">{`${RMultilingual(`TEAM_STATUS.WORDING`)}`}</h4>
        <div className="status--list">
          {(registry.fee === 0 ? FreeTournamentStatusList : PaidTournamentStatusList).map((status, i) => (
            <ArrowDiv key={status} isCurrent={teamInRegistry.status === status}>
              <span>{`${(i + 1).toString()}. ${RMultilingual(`TEAM_STATUS.${status}`)}`}</span>
            </ArrowDiv>
          ))}
        </div>
        <div className="status--action">
          {getActionButton()}
          <Button width="fit-content" color="blue" onClick={collapseProgramPrint}>
            {RMultilingual(`PRINT.WORDING`)}
          </Button>
        </div>
        {showProgramPrint && <ProgramPrint teamInRegistry={teamInRegistry} registry={registry} />}
      </div>
    </StyledDiv>
  );
};

export default ReviewSection;
